import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchDashboardV2Financial,
  fetchDashboardV2Network,
  fetchDashboardV2Overview,
  fetchDashboardV2Roadblocks,
  fetchDashboardV2Shops,
  type AgentDashboardFinancialData,
  type AgentDashboardNetworkData,
  type AgentDashboardOverviewData,
  type AgentDashboardRoadblocksData,
  type AgentDashboardShopsData,
  type DashboardV2Period,
} from '../services/api';

type QueryState<T> = {
  data: T | null;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  refreshing: boolean;
  refetch: () => Promise<void>;
};

type Fetcher<T> = (
  period: DashboardV2Period,
  refresh?: boolean,
  extra?: Record<string, unknown>,
) => Promise<T>;

/**
 * Stable dashboard fetcher hook.
 * IMPORTANT: do not put `fetcher` in effect deps — inline lambdas from callers
 * would recreate every render and spam the API.
 */
function useDashboardV2Query<T>(
  fetcher: Fetcher<T>,
  period: DashboardV2Period,
  extra?: Record<string, unknown>,
  enabled = true,
): QueryState<T> {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(enabled);
  const [isFetching, setIsFetching] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const extraKey = JSON.stringify(extra ?? null);
  const requestIdRef = useRef(0);

  const load = useCallback(
    async (refresh = false, silent = false) => {
      if (!enabled) {
        setIsLoading(false);
        setIsFetching(false);
        setRefreshing(false);
        return;
      }
      const requestId = ++requestIdRef.current;
      if (refresh) setRefreshing(true);
      else if (!silent) setIsLoading(true);
      setIsFetching(true);
      setError(null);
      try {
        const parsedExtra = JSON.parse(extraKey) as Record<string, unknown> | null;
        const result = await fetcherRef.current(period, refresh, parsedExtra ?? undefined);
        if (requestId !== requestIdRef.current) return;
        setData(result);
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        const e = err instanceof Error ? err : new Error('Failed to load dashboard');
        setError(e); // surfaced inline by TabSection (error + retry)
      } finally {
        if (requestId === requestIdRef.current) {
          setIsLoading(false);
          setIsFetching(false);
          setRefreshing(false);
        }
      }
    },
    [enabled, period, extraKey],
  );

  useEffect(() => {
    void load(false);
  }, [load]);

  const refresh = useCallback(async () => load(true, true), [load]);
  const refetch = useCallback(async () => load(false, true), [load]);

  return { data, isLoading, isFetching, error, refresh, refreshing, refetch };
}

export const useDashboardV2Overview = (period: DashboardV2Period, tz?: string, enabled = true) =>
  useDashboardV2Query<AgentDashboardOverviewData>(
    (p, refresh) => fetchDashboardV2Overview(p, refresh, tz),
    period,
    tz ? { tz } : undefined,
    enabled,
  );

export const useDashboardV2Financial = (period: DashboardV2Period, tz?: string, enabled = true) =>
  useDashboardV2Query<AgentDashboardFinancialData>(
    (p, refresh) => fetchDashboardV2Financial(p, refresh, tz),
    period,
    tz ? { tz } : undefined,
    enabled,
  );

export const useDashboardV2Shops = (period: DashboardV2Period, page: number, tz?: string, enabled = true) =>
  useDashboardV2Query<AgentDashboardShopsData>(
    (p, refresh) => fetchDashboardV2Shops(p, { page, tz }, refresh),
    period,
    { page, tz: tz ?? null },
    enabled,
  );

export const useDashboardV2Network = (period: DashboardV2Period, tz?: string, enabled = true) =>
  useDashboardV2Query<AgentDashboardNetworkData>(
    (p, refresh) => fetchDashboardV2Network(p, refresh, tz),
    period,
    tz ? { tz } : undefined,
    enabled,
  );

export const useDashboardV2Roadblocks = (period: DashboardV2Period, tz?: string, enabled = true) =>
  useDashboardV2Query<AgentDashboardRoadblocksData>(
    (p, refresh) => fetchDashboardV2Roadblocks(p, refresh, tz),
    period,
    tz ? { tz } : undefined,
    enabled,
  );
